import { RefObject, useEffect } from 'react';
import { SupportSession } from '../../../services/supportApi';
import { useEngineerRemoteSupport } from '../../../context/EngineerRemoteSupportProvider';

interface RemoteSessionViewProps {
  session: SupportSession;
  remoteVideoRef: RefObject<HTMLVideoElement | null>;
  onControlClick?: (x: number, y: number) => void;
}

export function RemoteSessionView({ session, remoteVideoRef, onControlClick }: RemoteSessionViewProps) {
  const { remoteStream } = useEngineerRemoteSupport();
  const controlEnabled = session.permissions?.controlPortal === true;

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      if (remoteVideoRef.current.srcObject !== remoteStream) {
        remoteVideoRef.current.srcObject = remoteStream;
        void remoteVideoRef.current.play().catch(() => undefined);
      }
    }
  }, [remoteStream, remoteVideoRef]);

  if (session.guacamole?.enabled && session.guacamole.url) {
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-black min-h-[420px]">
        <iframe
          title="Guacamole Remote Session"
          src={session.guacamole.url}
          className="w-full h-[520px] border-0"
          allow="clipboard-read; clipboard-write"
        />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-black min-h-[420px] relative">
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="w-full h-[520px] object-contain bg-black"
        onClick={(e) => {
          if (!controlEnabled || !onControlClick) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          onControlClick(x, y);
        }}
      />
      {!session.permissions?.viewScreen && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-sm">
          Waiting for customer screen share…
        </div>
      )}
      {controlEnabled && (
        <div className="absolute bottom-3 left-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
          Control mode — click on video to relay pointer events
        </div>
      )}
    </div>
  );
}
